import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X, CalendarRange, Users, Award, Command, ArrowRight, Compass, History } from "lucide-react";
import api from "../lib/api";
import NotificationPanel from "./kit/NotificationPanel";
import { NAV_BY_ROLE, flattenNav } from "./DashboardLayout";
import { recordVisit, topPaths } from "../lib/nav-history";

/**
 * Global search + notifications that slot into the dashboard top bar.
 *
 * ⌘K / Ctrl+K opens a command palette that searches in TWO planes:
 *   1. **Navigate** — every sidebar item available to the current role
 *      (instant, client-side fuzzy match — no API call).
 *   2. **Content** — events / delegates / certificates via the
 *      ``/v2/...`` search endpoints (debounced 180 ms).
 *
 * Mobile-aware: on viewports < ``md`` the desktop search "pill" is
 * replaced by a single ``Compass`` icon button that opens the same
 * palette as a full-screen modal.  Same shortcut, same data —
 * responsive UX without a second component.
 */
export default function TopBarTools({ user }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ events: [], delegates: [], certificates: [] });
  const [notifs, setNotifs] = useState([]);
  const [readIds, setReadIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("factskoot_notif_read") || "[]")); }
    catch { return new Set(); }
  });
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Flatten this user's role-specific sidebar so the palette can
  // surface navigation matches.  Memoised — recomputes only on role
  // change.
  const navItems = useMemo(() => {
    const cfg = NAV_BY_ROLE[user?.role] || NAV_BY_ROLE.delegate;
    return flattenNav(cfg);
  }, [user?.role]);

  const filteredNav = useMemo(() => {
    if (!q || q.length === 0) {
      // Empty query → show the user's top-5 most-visited pages,
      // falling back to the first 6 items when history is too thin.
      const { items, kind } = topPaths(navItems, 5);
      return { items, kind };
    }
    const needle = q.toLowerCase().trim();
    return {
      items: navItems.filter((i) => i.label.toLowerCase().includes(needle)).slice(0, 8),
      kind: "search",
    };
  }, [q, navItems]);

  // Cmd+K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 50);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Fetch notifications on mount
  useEffect(() => {
    const canFetch = ["super_admin", "org_admin", "event_manager"].includes(user?.role);
    if (canFetch) {
      api.get("/notifications").then((r) => {
        setNotifs(Array.isArray(r.data) ? r.data : []);
      }).catch(() => {});
    }
  }, [user?.role]);

  // Debounced search
  useEffect(() => {
    if (!open || !q || q.length < 2) {
      setResults({ events: [], delegates: [], certificates: [] });
      return;
    }
    const t = setTimeout(() => {
      Promise.all([
        api.get(`/v2/events?search=${encodeURIComponent(q)}&limit=4`).catch(() => ({ data: { items: [] } })),
        api.get(`/v2/delegates?search=${encodeURIComponent(q)}&limit=4`).catch(() => ({ data: { items: [] } })),
        api.get(`/v2/certificates?search=${encodeURIComponent(q)}&limit=4`).catch(() => ({ data: { items: [] } })),
      ]).then(([e, d, c]) => {
        setResults({
          events: e.data.items || [],
          delegates: d.data.items || [],
          certificates: c.data.items || [],
        });
      });
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const enriched = notifs.map((n) => ({ ...n, read: readIds.has(n.id), channel: n.channel }));

  const markAllRead = () => {
    const ids = notifs.map((n) => n.id);
    const next = new Set([...readIds, ...ids]);
    setReadIds(next);
    localStorage.setItem("factskoot_notif_read", JSON.stringify([...next]));
  };

  const onNotifClick = (n) => {
    const next = new Set(readIds);
    next.add(n.id);
    setReadIds(next);
    localStorage.setItem("factskoot_notif_read", JSON.stringify([...next]));
  };

  return (
    <>
      {/* Desktop search pill */}
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="hidden md:flex items-center gap-2 px-3.5 h-10 rounded-full bg-slate-100 border border-slate-200 text-slate-500 hover:text-[#0A192F] hover:bg-white hover:border-slate-300 transition-colors"
        data-testid="global-search-trigger"
      >
        <Search className="h-4 w-4" />
        <span className="text-xs">Jump to a page or search…</span>
        <span className="ml-3 hidden lg:flex items-center gap-1 text-[10px] font-mono text-slate-400 border border-slate-300 rounded px-1.5 py-0.5">
          <Command className="h-2.5 w-2.5" /> K
        </span>
      </button>

      {/* Mobile / tablet — a single compass icon that opens the palette
          as a full-screen modal.  Keyboard-shortcut still works on
          tablets with hardware keyboards. */}
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full bg-slate-100 border border-slate-200 text-slate-600 hover:text-[#0A192F] hover:bg-white transition-colors"
        data-testid="global-search-trigger-mobile"
        aria-label="Search and navigate"
      >
        <Compass className="h-4 w-4" />
      </button>

      <NotificationPanel
        items={enriched}
        onMarkAllRead={markAllRead}
        onItemClick={onNotifClick}
      />

      {open && (
        <div
          className="fixed inset-0 z-50 bg-[#0A192F]/60 backdrop-blur-sm flex items-start justify-center pt-[8vh] sm:pt-[10vh] px-3 sm:px-4"
          onClick={() => setOpen(false)}
          data-testid="global-search-modal"
        >
          <div
            className="w-full max-w-2xl rounded-xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 border-b border-slate-200">
              <Search className="h-5 w-5 text-slate-400" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Jump to a page or search events, delegates, certificates…"
                className="flex-1 h-14 bg-transparent text-sm outline-none placeholder:text-slate-400"
                data-testid="global-search-input"
                autoFocus
              />
              <button
                onClick={() => setOpen(false)}
                className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center"
                data-testid="global-search-close"
                aria-label="Close search"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>
            <div className="max-h-[70vh] sm:max-h-[60vh] overflow-auto divide-y divide-slate-100">
              {/* Navigation results — always shown.  Top 5 most-visited
                  when empty (or starter list for new users), filtered
                  on the user's query otherwise. */}
              <NavResultGroup
                title={
                  q.length === 0
                    ? (filteredNav.kind === "history" ? "Frequent" : "Jump to")
                    : "Pages"
                }
                kind={q.length === 0 ? filteredNav.kind : "search"}
                items={filteredNav.items}
                onSelect={(it) => { recordVisit(it.to); setOpen(false); navigate(it.to); }}
              />
              {/* Content search — only fires for q.length >= 2 */}
              {q.length >= 2 && (
                <>
                  <ResultGroup title="Events" icon={CalendarRange} items={results.events} render={(e) => (
                    <Link to={`/dashboard/events/${e.id}`} onClick={() => setOpen(false)} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50" data-testid={`search-event-${e.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-[#0A192F]">{e.title}</p>
                        <p className="text-xs text-slate-500">{e.location} · {e.format}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">{e.status}</span>
                    </Link>
                  )} />
                  <ResultGroup title="Delegates" icon={Users} items={results.delegates} render={(d) => (
                    <div className="px-5 py-3 hover:bg-slate-50" data-testid={`search-delegate-${d.id}`}>
                      <p className="text-sm font-semibold text-[#0A192F]">{d.name}</p>
                      <p className="text-xs text-slate-500">{d.email}</p>
                    </div>
                  )} />
                  <ResultGroup title="Certificates" icon={Award} items={results.certificates} render={(c) => (
                    <Link to={`/verify/${c.code}`} target="_blank" onClick={() => setOpen(false)} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50" data-testid={`search-cert-${c.code}`}>
                      <div>
                        <p className="text-sm font-semibold text-[#0A192F]">{c.delegate_name} · {c.award_type}</p>
                        <p className="text-xs text-slate-500 font-mono">{c.code}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF37]">verify →</span>
                    </Link>
                  )} />
                  {!results.events.length && !results.delegates.length && !results.certificates.length && !filteredNav.items.length && (
                    <div className="p-8 text-center text-xs text-slate-500">No matches found.</div>
                  )}
                </>
              )}
            </div>
            <div className="px-4 py-2.5 border-t border-slate-100 text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold flex items-center justify-between flex-wrap gap-2">
              <span className="flex items-center gap-2">
                <kbd className="font-mono border border-slate-300 rounded px-1.5 py-0.5 text-slate-500 normal-case">↵</kbd> open ·
                <kbd className="font-mono border border-slate-300 rounded px-1.5 py-0.5 text-slate-500 normal-case">ESC</kbd> close
              </span>
              <span className="hidden sm:inline">Powered by Diplomexa search</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function NavResultGroup({ title, kind = "search", items, onSelect }) {
  if (!items?.length) return null;
  const HeaderIcon = kind === "history" ? History : Compass;
  return (
    <div data-testid="search-nav-group">
      <p className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1.5">
        <HeaderIcon className="h-3 w-3 text-[#D4AF37]" /> {title}
        {kind === "history" && (
          <span className="ml-1 text-[9px] tracking-[0.18em] text-slate-400 normal-case font-medium">
            — your most-visited pages
          </span>
        )}
      </p>
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <button
            type="button"
            key={it.to}
            onClick={() => onSelect(it)}
            className="w-full text-left flex items-center gap-3 px-5 py-2.5 hover:bg-slate-50 group transition-colors"
            data-testid={`search-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <span className="h-7 w-7 rounded-md bg-slate-100 group-hover:bg-[#D4AF37]/15 flex items-center justify-center transition-colors">
              {Icon ? <Icon className="h-3.5 w-3.5 text-slate-600 group-hover:text-[#D4AF37]" /> : null}
            </span>
            <span className="flex-1 text-sm font-medium text-[#0A192F]">{it.label}</span>
            <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-[#D4AF37] transition-colors" />
          </button>
        );
      })}
    </div>
  );
}

function ResultGroup({ title, icon: Icon, items, render }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className="px-5 pt-3 pb-1 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 flex items-center gap-1">
        <Icon className="h-3 w-3 text-[#D4AF37]" /> {title}
      </p>
      {items.map((it) => (
        <div key={it.id || it.code}>{render(it)}</div>
      ))}
    </div>
  );
}
